import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ChevronLeft, 
  Building2, 
  Users, 
  BookOpen, 
  User, 
  Calendar, 
  Eye, 
  Mail, 
  Phone, 
  ChevronDown, 
  ChevronUp,
  Award,
  UserCheck
} from 'lucide-react';
import api from '../api';
import DepartmentDetailModal from '../Tenant-Admin/Department-Components/DepartmentDetailModal';
import HODHeader from './components/Header';

const HODDepartment = ({ tenantId, userId, token, userRole }) => {
  const navigate = useNavigate();
  
  const effectiveTenantId = tenantId || localStorage.getItem('tenantId');
  const effectiveUserId = userId || localStorage.getItem('userId');
  const effectiveToken = token || localStorage.getItem('token');
  const effectiveUserRole = userRole || localStorage.getItem('userRole') || 'hod';

  const [loading, setLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [error, setError] = useState(null);
  const [subjects, setSubjects] = useState({});
  const [expandedDept, setExpandedDept] = useState(null);

  // Modal display states
  const [detailsModal, setDetailsModal] = useState({
    isOpen: false,
    department: null
  });

  // Authorization setup
  const authHeaders = useMemo(() => {
    const headers = { Accept: 'application/json' };
    if (effectiveToken) headers.Authorization = `Bearer ${effectiveToken}`;
    return headers;
  }, [effectiveToken]);

  // Load department lists from API using /api/hod endpoint
  const fetchDepartments = useCallback(async () => {
    if (!effectiveTenantId) {
      setDepartments([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/api/hod/departments/${effectiveTenantId}`, { headers: authHeaders });
      
      const allDepartments = res?.data?.data ?? [];
      
      const hodDepartments = allDepartments.filter(dept => 
        dept.hodId === effectiveUserId || dept.hodId?.toString() === effectiveUserId
      );
      
      setDepartments(hodDepartments);
      
      if (hodDepartments.length > 0) {
        const firstDeptId = hodDepartments[0]._id || hodDepartments[0].id;
        setExpandedDept(firstDeptId);
      }
      
      for (const dept of hodDepartments) {
        await loadDepartmentSubjects(dept);
      }
    } catch (err) {
      console.error('Error fetching departments:', err);
      setError(err?.response?.data?.message || err.message || 'Failed to fetch departments');
      setDepartments([]);
    } finally {
      setLoading(false);
    }
  }, [effectiveTenantId, authHeaders, effectiveUserId]);

  // Load subjects for a department
  const loadDepartmentSubjects = async (dept) => {
    const deptId = dept._id || dept.id;
    try {
      const subjectsRes = await api.get(`/api/subjects/${deptId}`);
      const subjectList = subjectsRes?.data?.subjects || [];
      setSubjects(prev => ({ ...prev, [deptId]: subjectList }));
    } catch (err) {
      console.error(`Failed to load subjects for department:`, err);
    }
  };

  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  const openDetailsModal = (department) => {
    setDetailsModal({ isOpen: true, department });
  };

  const closeDetailsModal = () => {
    setDetailsModal({ isOpen: false, department: null });
  };

  const toggleExpand = (deptId) => {
    setExpandedDept(expandedDept === deptId ? null : deptId);
  };

  const handleUpdateSave = async ({ name }) => {
    if (!detailsModal.department) {
      throw new Error('No department selected for editing.');
    }
    
    const deptId = detailsModal.department._id || detailsModal.department.id;
    
    try {
      const updateData = { tenantId: effectiveTenantId, name };
      const res = await api.put(`/api/edit-department/${deptId}`, updateData, { headers: authHeaders });

      const updatedDepartment = res?.data?.department || {
        ...detailsModal.department,
        name,
        updatedAt: new Date().toISOString()
      };

      setDepartments(prev => prev.map(d => {
        const id = d._id || d.id;
        return String(id) === String(deptId) ? updatedDepartment : d;
      }));

      setDetailsModal(prev => ({ ...prev, department: updatedDepartment }));
      return updatedDepartment;
    } catch (err) {
      const message = err?.response?.data?.message || err.message || 'Update failed';
      throw new Error(message);
    }
  };

  // ✅ Format date in a readable way
  const formatDate = (dateString) => {
    if (!dateString) return 'Not available';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // ✅ Get initial for avatar
  const getInitial = (name) => {
    if (!name) return '?';
    return name.charAt(0).toUpperCase();
  };

  // ✅ Format date for display
  const getRelativeTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-gray-900 dark:to-gray-800">
      <HODHeader />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
        
        {/* Back Button & Header */}
        <div className="mb-6 sm:mb-8">
          <button
            onClick={() => navigate('/hod/dashboard')}
            className="inline-flex items-center gap-2 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors text-sm font-medium group mb-4"
          >
            <ChevronLeft size={18} className="group-hover:-translate-x-0.5 transition-transform" />
            Back to Dashboard
          </button>
          
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-white">
                My Department
              </h1>
              <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                Comprehensive overview of your department
              </p>
            </div>
            <div className="flex items-center gap-3 px-4 py-2 bg-emerald-50 dark:bg-emerald-900/30 rounded-full border border-emerald-200 dark:border-emerald-800">
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
              <span className="text-xs font-medium text-emerald-700 dark:text-emerald-300">
                {departments.length} Department{departments.length !== 1 ? 's' : ''} Assigned
              </span>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="relative">
              <div className="w-16 h-16 border-4 border-emerald-200 dark:border-emerald-800 rounded-full"></div>
              <div className="absolute top-0 left-0 w-16 h-16 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
            <p className="mt-4 text-sm font-medium text-slate-500 dark:text-slate-400 animate-pulse">
              Loading department details...
            </p>
          </div>
        )}

        {/* Error State */}
        {!loading && error && (
          <div className="bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 rounded-2xl p-8 text-center max-w-2xl mx-auto">
            <div className="w-16 h-16 bg-rose-100 dark:bg-rose-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-rose-600 dark:text-rose-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-rose-800 dark:text-rose-200 mb-2">Unable to Load Department</h3>
            <p className="text-sm text-rose-600 dark:text-rose-300 mb-4">{error}</p>
            <button
              onClick={fetchDepartments}
              className="inline-flex items-center gap-2 px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-sm font-medium transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Try Again
            </button>
          </div>
        )}

        {/* Empty State */}
        {!loading && departments.length === 0 && !error && (
          <div className="text-center py-20 bg-white dark:bg-gray-800 rounded-3xl border border-slate-200 dark:border-gray-700 shadow-sm max-w-2xl mx-auto">
            <div className="w-24 h-24 bg-slate-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-6">
              <Building2 size={40} className="text-slate-400 dark:text-slate-500" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">No Department Assigned</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 max-w-sm mx-auto leading-relaxed">
              You are not currently assigned as Head of Department for any department. 
              Please contact your administrator for assistance.
            </p>
          </div>
        )}

        {/* Department Cards */}
        {!loading && departments.length > 0 && (
          <div className="grid grid-cols-1 gap-6">
            {departments.map((dept) => {
              const deptId = dept._id || dept.id;
              const deptSubjects = subjects[deptId] || [];
              const isExpanded = expandedDept === deptId;
              const facultyCount = dept.facultyCount || 0;
              const hodName = dept.hodDetails?.name || dept.hodName || 'Not Assigned';
              const facultyList = dept.facultyDetails || [];
              const createdDate = formatDate(dept.createdAt);
              const relativeTime = getRelativeTime(dept.createdAt);
              
              return (
                <div
                  key={deptId}
                  className="bg-white dark:bg-gray-800 rounded-2xl border border-slate-200 dark:border-gray-700 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden"
                >
                  {/* Card Header */}
                  <div 
                    className="bg-gradient-to-r from-emerald-600 to-teal-600 px-5 sm:px-7 py-5 sm:py-6 cursor-pointer hover:from-emerald-700 hover:to-teal-700 transition-colors"
                    onClick={() => toggleExpand(deptId)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-4 min-w-0 flex-1">
                        <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-white/20 flex items-center justify-center text-white font-bold text-xl sm:text-2xl shadow-inner flex-shrink-0">
                          {dept.name?.charAt(0)?.toUpperCase() || 'D'}
                        </div>
                        <div className="min-w-0 flex-1">
                          <h2 className="text-lg sm:text-xl font-bold text-white truncate">
                            {dept.name}
                          </h2>
                          <div className="flex flex-wrap items-center gap-2 mt-1">
                            <span className="text-xs text-emerald-100 font-medium flex items-center gap-1">
                              <Users size={13} />
                              {facultyCount} Faculty
                            </span>
                            <span className="w-1 h-1 rounded-full bg-emerald-300/50 hidden sm:block"></span>
                            <span className="text-xs text-emerald-100 font-medium flex items-center gap-1">
                              <BookOpen size={13} />
                              {deptSubjects.length} Subjects
                            </span>
                            <span className="w-1 h-1 rounded-full bg-emerald-300/50 hidden sm:block"></span>
                            <span className="text-xs text-emerald-100 font-medium flex items-center gap-1">
                              <Calendar size={13} />
                              {relativeTime}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openDetailsModal(dept);
                          }}
                          className="p-2 bg-white/20 hover:bg-white/30 rounded-lg text-white transition-colors"
                          title="View Full Details"
                        >
                          <Eye size={18} />
                        </button>
                        {isExpanded ? (
                          <ChevronUp size={22} className="text-white" />
                        ) : (
                          <ChevronDown size={22} className="text-white" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Quick Stats Bar */}
                  <div className="grid grid-cols-3 divide-x divide-slate-100 dark:divide-gray-700 bg-slate-50/80 dark:bg-gray-900/60">
                    <div className="py-3 px-3 text-center">
                      <UserCheck size={16} className="text-emerald-600 dark:text-emerald-400 mx-auto mb-1" />
                      <p className="text-[10px] font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">HOD</p>
                      <p className="text-xs sm:text-sm font-semibold text-slate-800 dark:text-slate-200 truncate">
                        {hodName}
                      </p>
                    </div>
                    <div className="py-3 px-3 text-center">
                      <Users size={16} className="text-blue-600 dark:text-blue-400 mx-auto mb-1" />
                      <p className="text-[10px] font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Faculty</p>
                      <p className="text-xs sm:text-sm font-bold text-slate-800 dark:text-slate-200">
                        {facultyCount} Members
                      </p>
                    </div>
                    <div className="py-3 px-3 text-center">
                      <BookOpen size={16} className="text-purple-600 dark:text-purple-400 mx-auto mb-1" />
                      <p className="text-[10px] font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Subjects</p>
                      <p className="text-xs sm:text-sm font-bold text-slate-800 dark:text-slate-200">
                        {deptSubjects.length} Offered
                      </p>
                    </div>
                  </div>

                  {/* Expanded Details - NO RAW IDs */}
                  {isExpanded && (
                    <div className="p-5 sm:p-7 border-t border-slate-100 dark:border-gray-700 animate-fadeIn">
                      <div className="space-y-6">
                        {/* Department Info - WITHOUT ID */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="bg-slate-50 dark:bg-gray-700/50 p-4 rounded-xl">
                            <p className="text-[11px] font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider flex items-center gap-2">
                              <Award size={14} className="text-emerald-600" />
                              Department Name
                            </p>
                            <p className="text-sm font-semibold text-slate-900 dark:text-white mt-1">
                              {dept.name}
                            </p>
                          </div>
                          <div className="bg-slate-50 dark:bg-gray-700/50 p-4 rounded-xl">
                            <p className="text-[11px] font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider flex items-center gap-2">
                              <Calendar size={14} className="text-emerald-600" />
                              Established
                            </p>
                            <p className="text-sm font-semibold text-slate-900 dark:text-white mt-1">
                              {createdDate}
                            </p>
                            <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">
                              {relativeTime}
                            </p>
                          </div>
                        </div>

                        {/* Department Description */}
                        {dept.description && (
                          <div className="bg-slate-50 dark:bg-gray-700/50 p-4 rounded-xl">
                            <p className="text-[11px] font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Description</p>
                            <p className="text-sm text-slate-700 dark:text-slate-300 mt-1 leading-relaxed">
                              {dept.description}
                            </p>
                          </div>
                        )}

                        {/* HOD Details */}
                        {dept.hodDetails && (
                          <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl p-4">
                            <p className="text-[11px] font-medium text-emerald-600 dark:text-emerald-400 uppercase tracking-wider flex items-center gap-2">
                              <User size={14} />
                              Head of Department
                            </p>
                            <div className="flex items-start gap-4 mt-2">
                              <div className="w-12 h-12 rounded-full bg-emerald-600 flex items-center justify-center text-white font-bold text-base flex-shrink-0">
                                {getInitial(dept.hodDetails.name)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-base font-semibold text-emerald-900 dark:text-emerald-100">
                                  {dept.hodDetails.name}
                                </p>
                                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 mt-1">
                                  {dept.hodDetails.email && (
                                    <p className="text-xs text-emerald-700 dark:text-emerald-300 flex items-center gap-1.5">
                                      <Mail size={13} className="flex-shrink-0" />
                                      <span className="truncate">{dept.hodDetails.email}</span>
                                    </p>
                                  )}
                                  {dept.hodDetails.phoneNumber && (
                                    <p className="text-xs text-emerald-700 dark:text-emerald-300 flex items-center gap-1.5">
                                      <Phone size={13} className="flex-shrink-0" />
                                      {dept.hodDetails.phoneNumber}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Faculty List with Full Details - NO IDs */}
                        {facultyList.length > 0 && (
                          <div>
                            <h4 className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                              <Users size={15} className="text-emerald-600" />
                              Faculty Team ({facultyList.length} Members)
                            </h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              {facultyList.map((faculty, index) => (
                                <div
                                  key={faculty._id || index}
                                  className="bg-slate-50 dark:bg-gray-700/50 rounded-xl p-4 border border-slate-200 dark:border-gray-600 hover:shadow-md transition-shadow"
                                >
                                  <div className="flex items-start gap-3">
                                    <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-700 dark:text-emerald-300 font-bold text-sm flex-shrink-0">
                                      {getInitial(faculty.name)}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-semibold text-slate-900 dark:text-white truncate">
                                        {faculty.name}
                                      </p>
                                      <div className="space-y-1 mt-1">
                                        {faculty.email && (
                                          <p className="text-xs text-slate-500 dark:text-slate-400 truncate flex items-center gap-1.5">
                                            <Mail size={12} className="flex-shrink-0 text-slate-400" />
                                            {faculty.email}
                                          </p>
                                        )}
                                        {faculty.phoneNumber && (
                                          <p className="text-xs text-slate-500 dark:text-slate-400 truncate flex items-center gap-1.5">
                                            <Phone size={12} className="flex-shrink-0 text-slate-400" />
                                            {faculty.phoneNumber}
                                          </p>
                                        )}
                                        {faculty.role && (
                                          <span className="inline-block mt-1 text-[10px] font-medium text-slate-500 dark:text-slate-400 bg-slate-200 dark:bg-gray-600 px-2 py-0.5 rounded-full">
                                            {faculty.role}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Subjects List */}
                        {deptSubjects.length > 0 && (
                          <div>
                            <h4 className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                              <BookOpen size={15} className="text-emerald-600" />
                              Subjects Offered ({deptSubjects.length})
                            </h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {deptSubjects.map((subject) => (
                                <div key={subject._id} className="flex items-center justify-between p-2.5 bg-slate-50 dark:bg-gray-700/30 rounded-lg border border-slate-100 dark:border-gray-600">
                                  <span className="text-sm font-medium text-slate-900 dark:text-white truncate flex-1">
                                    {subject.name}
                                  </span>
                                  {subject.subjectCode && (
                                    <span className="text-xs text-slate-500 dark:text-slate-400 font-mono ml-2 flex-shrink-0 bg-slate-200 dark:bg-gray-600 px-2 py-0.5 rounded">
                                      {subject.subjectCode}
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* View Full Details Button */}
                        <button
                          onClick={() => openDetailsModal(dept)}
                          className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium text-sm transition-colors flex items-center justify-center gap-2"
                        >
                          <Eye size={16} />
                          View Complete Department Profile
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Footer */}
        <div className="mt-10 pt-6 border-t border-slate-200 dark:border-gray-700">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-xs text-slate-400 dark:text-slate-500">
              HOD Dashboard • {departments.length} Department{departments.length !== 1 ? 's' : ''} Assigned
            </p>
            <p className="text-xs text-slate-400 dark:text-slate-500">
              © {new Date().getFullYear()} All rights reserved
            </p>
          </div>
        </div>
      </div>

      {/* Department Detail Modal */}
      <DepartmentDetailModal
        isOpen={detailsModal.isOpen}
        onClose={closeDetailsModal}
        department={detailsModal.department}
        faculty={detailsModal.department ? detailsModal.department.facultyDetails || [] : []}
        subjects={detailsModal.department ? subjects[detailsModal.department._id || detailsModal.department.id] || [] : []}
        onSaveDepartment={handleUpdateSave}
        userRole={effectiveUserRole}
      />
    </div>
  );
};

export default HODDepartment;